import React, { useId } from 'react'
import { theme } from '@notino/react-styleguide'
import { useIntl } from 'react-intl'

// utils
import { PARTIAL_RATING_OPTIONS } from '../../utils/helper'
import { PARTIAL_RATING } from '../../utils/enums'

// types
import { SalonPartialRatingsProps } from './types'

// components
import VisuallyHidden from '../../atoms/VisuallyHidden/VisuallyHidden'

// styles
import * as SC from './SalonPartialRatingsStyles'

const FILLED_COLOR = theme.color.icon.primary
const EMPTY_COLOR = theme.color.common.gray90
const WIDTH = '100%'
const HEIGHT = '0.25rem'
/** Min. výška klikateľnej zóny; vizuálny pásik ostáva 0.25rem */
const MIN_HIT_HEIGHT = '0.5rem'
const SPACE = 2
const SEGMENTS = 5

const DEFAULT_VALUE = {
	[PARTIAL_RATING.SERVICE]: 0,
	[PARTIAL_RATING.PLACE]: 0,
	[PARTIAL_RATING.COMMUNICATION]: 0,
	[PARTIAL_RATING.GENERAL_IMPRESSION]: 0
}

const SalonPartialRatings = (props: SalonPartialRatingsProps) => {
	const { value, onChange, readOnly, roundReadOnlyValues = false, twoColLayoutOnDesktop = true } = props
	const intl = useIntl()
	const labelIdPrefix = useId()

	const partialRatings = PARTIAL_RATING_OPTIONS()

	const getStarsAriaLabel = (starCount: number) =>
		intl.formatMessage(
			{
				id: '{starCount, plural, one {# star} other {# stars}}',
				defaultMessage: '{starCount, plural, one {# star} other {# stars}}'
			},
			{ starCount }
		)

	const invisibleItemLabels = Array.from({ length: SEGMENTS }, (_, index) => getStarsAriaLabel(index + 1))

	const handleChange = (newRating: number, currentRating: PARTIAL_RATING) => {
		if (!onChange) {
			return
		}

		const newValue = partialRatings.reduce<NonNullable<SalonPartialRatingsProps['value']>>((acc, cv) => {
			if (cv.value === currentRating) {
				return { ...acc, [cv.value]: newRating }
			}

			if (value && value[cv.value]) {
				return { ...acc, [cv.value]: value[cv.value] }
			}

			return acc
		}, DEFAULT_VALUE)

		onChange(newValue)
	}

	if (readOnly) {
		return (
			<SC.RatingsDescriptionList $twoColLayoutOnDesktop={twoColLayoutOnDesktop}>
				{partialRatings.map((rating) => {
					const partialValue = roundReadOnlyValues
						? Math.round(value?.[rating.value] ?? DEFAULT_VALUE[rating.value])
						: (value?.[rating.value] ?? DEFAULT_VALUE[rating.value])
					const percentage = (partialValue / 5) * 100

					// Calculate the width of each segment, including space
					const totalSpace = (SEGMENTS - 1) * SPACE
					const segmentWidth = `calc((${WIDTH} - ${totalSpace}px) / ${SEGMENTS})`

					// Calculate the number of fully filled SEGMENTS and partial fill percentage
					const filledSegments = Math.floor((percentage / 100) * SEGMENTS)
					const partialFill = ((percentage / 100) * SEGMENTS - filledSegments) * 100

					return (
						<SC.RatingDescriptionRow key={rating.value}>
							<SC.RatingTerm>{rating.label}</SC.RatingTerm>
							<SC.RatingDescription>
								<SC.ProgressBarContainer aria-hidden={'true'}>
									<SC.ProgressBar $width={WIDTH} $height={HEIGHT}>
										{Array.from({ length: SEGMENTS }).map((_, index) => {
											const isFilled = index < filledSegments
											const isPartial = index === filledSegments

											return (
												<SC.Segment
													key={index}
													$width={segmentWidth}
													$height={HEIGHT}
													$backgroundColor={isFilled ? FILLED_COLOR : EMPTY_COLOR}
													$marginRight={index < SEGMENTS - 1 ? `${SPACE}px` : '0'}
												>
													{isPartial && (
														<SC.FilledPart
															$width={`${partialFill}%`}
															$filledColor={FILLED_COLOR}
															$isFirst={index === 0}
															$isLast={index === SEGMENTS - 1}
														/>
													)}
												</SC.Segment>
											)
										})}
									</SC.ProgressBar>
								</SC.ProgressBarContainer>
								<SC.RatingValue aria-hidden={'true'}>{partialValue}</SC.RatingValue>
								<VisuallyHidden>{getStarsAriaLabel(Math.round(partialValue))}</VisuallyHidden>
							</SC.RatingDescription>
						</SC.RatingDescriptionRow>
					)
				})}
			</SC.RatingsDescriptionList>
		)
	}

	return (
		<SC.RatingsList $twoColLayoutOnDesktop={twoColLayoutOnDesktop}>
			{partialRatings.map((rating) => {
				const partialValue = Math.round(value?.[rating.value] ?? DEFAULT_VALUE[rating.value])
				const labelId = `${labelIdPrefix}-${rating.value}`

				return (
					<SC.RatingRow key={rating.value}>
						<SC.LabelCol>
							<SC.RatingLabel id={labelId}>{rating.label}</SC.RatingLabel>
						</SC.LabelCol>
						<SC.RatingCol>
							<SC.Rate
								key={rating.value}
								$width={WIDTH}
								$height={HEIGHT}
								$minHitHeight={MIN_HIT_HEIGHT}
								$filledColor={FILLED_COLOR}
								$backgroundColor={EMPTY_COLOR}
								$space={SPACE}
								value={partialValue}
								items={SEGMENTS}
								spaceBetween={'none'}
								spaceInside={'none'}
								onChange={(newRating: number) => handleChange(newRating, rating.value)}
								visibleLabelId={labelId}
								invisibleItemLabels={invisibleItemLabels}
								isRequired
							/>
							<SC.RatingValue aria-hidden={'true'}>{partialValue}</SC.RatingValue>
						</SC.RatingCol>
					</SC.RatingRow>
				)
			})}
		</SC.RatingsList>
	)
}

export default SalonPartialRatings
